export const DISPLAY_CONFIGURATION_SCHEMA_VERSION = 3;
export const GOLDEN_DISPLAY_COLOR = 0xEFBF04FF;

export const normalizeDisplayID = (value?: string | null) =>
    (value ?? '').toLocaleLowerCase();

export const hasAssetTagMembership = (assetSet?: Pick<IAssetSet, 'AssetTagIDs'> | null): boolean =>
    (assetSet?.AssetTagIDs?.length ?? 0) > 0;

export const isSpecialAssetSetType = (type: AssetSetType): boolean =>
    type === AssetSetType.All3DAssets || type === AssetSetType.AllAssetTags;
export const is3DFallbackAssetSet = (assetSet?: Pick<IAssetSet, 'Type'> | null): boolean =>
    assetSet?.Type === AssetSetType.All3DAssets;
export const isAssetTagFallbackAssetSet = (assetSet?: Pick<IAssetSet, 'Type'> | null): boolean =>
    assetSet?.Type === AssetSetType.AllAssetTags;

export type AssetSetMembershipBadge = 'tags' | '3d' | null;

export const getAssetSetMembershipBadge = (
    assetSet?: Pick<IAssetSet, 'AssetTagIDs' | 'HierarchyItems'> | null
): AssetSetMembershipBadge => {
    const hasAssetTags = (assetSet?.AssetTagIDs?.length ?? 0) > 0;
    const has3DAssets = (assetSet?.HierarchyItems?.length ?? 0) > 0;

    if (hasAssetTags === has3DAssets) return null;
    return hasAssetTags ? 'tags' : '3d';
};

type AssetSetKindMembership = Pick<
    IAssetSet,
    'Type' | 'AssetTagIDs' | 'HierarchyItems' | 'AssetTagQuery' | 'HierarchyItemQuery'
>;

/**
 * The kinds a set declares, which is what the kind filters must read: a Smart set is defined by
 * its queries, so a tag query matching nothing today is still tag-based, while a Fixed set is
 * defined by the members it holds.
 */
const getAssetSetMembershipKinds = (assetSet: AssetSetKindMembership) => {
    const isQueryAssetSet = assetSet.Type === AssetSetType.Query;
    return {
        hasAssetTags: isQueryAssetSet
            ? !!assetSet.AssetTagQuery?.trim()
            : (assetSet.AssetTagIDs?.length ?? 0) > 0,
        has3DAssets: isQueryAssetSet
            ? !!assetSet.HierarchyItemQuery?.trim()
            : (assetSet.HierarchyItems?.length ?? 0) > 0,
    };
};

/**
 * A set is shown while any kind it declares is shown. A set declaring neither kind belongs to no
 * kind at all, so either filter lists it rather than one of them owning it by default.
 */
export const matchesAssetSetKindFilter = (
    assetSet: AssetSetKindMembership,
    showTagBasedAssetSets: boolean,
    show3DOnlyAssetSets: boolean
): boolean => {
    const { hasAssetTags, has3DAssets } = getAssetSetMembershipKinds(assetSet);
    if (!hasAssetTags && !has3DAssets) return showTagBasedAssetSets || show3DOnlyAssetSets;
    return (hasAssetTags && showTagBasedAssetSets) || (has3DAssets && show3DOnlyAssetSets);
};

/** Smart compatibility follows query intent, not the refreshable result cache. */
export const isPanoCompatibleAssetSet = (assetSet: IAssetSet): boolean => {
    if (isAssetTagFallbackAssetSet(assetSet)) return true;
    if (is3DFallbackAssetSet(assetSet)) return false;
    if (assetSet.Type === AssetSetType.Query) return !!assetSet.AssetTagQuery?.trim();
    return assetSet.Type === AssetSetType.List && hasAssetTagMembership(assetSet);
};

/** Assignment/rendering input only. Keep incompatible sets in the priority-row lookup. */
export const getPanoCompatibleAssetSets = (
    assetSets: readonly IAssetSet[]
): IAssetSet[] => assetSets.filter(isPanoCompatibleAssetSet);

export const getPanoInvalidAssetSetIDs = (assetSets: readonly IAssetSet[]): ReadonlySet<string> =>
    new Set(assetSets.filter(set => !isSpecialAssetSetType(set.Type) && !isPanoCompatibleAssetSet(set))
        .map(set => normalizeDisplayID(set.ID)));

/** Display invalid rows last without changing their stored priority or appearance. */
export const orderDisplayEntriesForPresentation = (
    entries: readonly IAssetSetDisplayEntry[],
    invalidIDs: ReadonlySet<string>,
): IAssetSetDisplayEntry[] => [
    ...entries.filter(entry => !invalidIDs.has(normalizeDisplayID(entry.AssetSetID))),
    ...entries.filter(entry => invalidIDs.has(normalizeDisplayID(entry.AssetSetID))),
];

export const isDisplayConfigurationCleared = (configuration: IAssetSetDisplayConfiguration): boolean =>
    configuration.Entries.every(entry =>
        (normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(configuration.All3DAssetsID) ||
            normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(configuration.AllAssetTagsID)) &&
        entry.Mode === 'default' && entry.Color == null);

/** Move among editable rows only; invalid rows retain their saved slots for recovery. */
export const reorderValidDisplayEntry = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string,
    hoveredAssetSetID: string,
    after: boolean,
    invalidIDs: ReadonlySet<string>,
): IAssetSetDisplayConfiguration => {
    const specialIDs = new Set([normalizeDisplayID(source.All3DAssetsID), normalizeDisplayID(source.AllAssetTagsID)]);
    const isMovable = (entry: IAssetSetDisplayEntry) => {
        const id = normalizeDisplayID(entry.AssetSetID);
        return !specialIDs.has(id) && !invalidIDs.has(id);
    };
    const movable = source.Entries.filter(isMovable);
    const from = movable.findIndex(entry => normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(assetSetID));
    const hovered = movable.findIndex(entry => normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(hoveredAssetSetID));
    if (from < 0 || hovered < 0 || from === hovered) return source;
    const insertion = hovered + (after ? 1 : 0);
    const target = insertion - (from < insertion ? 1 : 0);
    if (from === target) return source;
    const [entry] = movable.splice(from, 1);
    movable.splice(target, 0, entry);
    let index = 0;
    return {
        ...source,
        Entries: source.Entries.map((saved, ordinal) => ({
            ...(isMovable(saved) ? movable[index++] : saved),
            Ord: ordinal + 1,
        })),
    };
};

const isMode = (value: unknown): value is AssetSetDisplayMode =>
    value === 'default' || value === 'color' || value === 'hidden';

const normalizedColor = (value?: number | null): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value >>> 0 : null;

const sameEntries = (left: readonly IAssetSetDisplayEntry[], right: readonly IAssetSetDisplayEntry[]) =>
    left.length === right.length && left.every((entry, index) => {
        const other = right[index];
        return !!other && entry.AssetSetID === other.AssetSetID && entry.Mode === other.Mode &&
            (entry.Color ?? null) === (other.Color ?? null) &&
            (entry.MaterialID ?? null) === (other.MaterialID ?? null) && entry.Ord === other.Ord;
    });

export const createDisplayConfiguration = (
    facilityID: string,
    all3DAssetsID: string,
    allAssetTagsID: string
): IAssetSetDisplayConfiguration => ({
    SchemaVersion: DISPLAY_CONFIGURATION_SCHEMA_VERSION,
    FacilityID: facilityID,
    All3DAssetsID: all3DAssetsID,
    AllAssetTagsID: allAssetTagsID,
    Entries: [
        { AssetSetID: all3DAssetsID, Mode: 'default', Color: null, Ord: 1 },
        { AssetSetID: allAssetTagsID, Mode: 'default', Color: null, Ord: 2 },
    ],
});

export const cloneDisplayConfiguration = (
    configuration: IAssetSetDisplayConfiguration
): IAssetSetDisplayConfiguration => ({
    ...configuration,
    Entries: configuration.Entries.map(entry => ({ ...entry })),
});

export const reconcileDisplayEntries = (
    source: IAssetSetDisplayConfiguration,
    allFacilitySets: readonly IAssetSet[],
    all3DAssetsID: string,
    allAssetTagsID: string
): IAssetSetDisplayConfiguration => {
    const setsByID = new Map(allFacilitySets
        .filter(set => !set.KnowledgeBookID)
        .map(set => [normalizeDisplayID(set.ID), set] as const));
    const normalized3DID = normalizeDisplayID(all3DAssetsID);
    const normalizedTagsID = normalizeDisplayID(allAssetTagsID);
    const all3DAssets = setsByID.get(normalized3DID);
    const allAssetTags = setsByID.get(normalizedTagsID);
    if (!is3DFallbackAssetSet(all3DAssets) || !isAssetTagFallbackAssetSet(allAssetTags)) return source;
    const specialKeys = new Set([normalized3DID, normalizedTagsID]);

    const seen = new Set<string>();
    const entries: IAssetSetDisplayEntry[] = [];
    for (const candidate of source.Entries ?? []) {
        const key = normalizeDisplayID(candidate.AssetSetID);
        const assetSet = setsByID.get(key);
        if (!assetSet || seen.has(key) || specialKeys.has(key) || isSpecialAssetSetType(assetSet.Type)) continue;
        seen.add(key);
        entries.push({
            AssetSetID: assetSet.ID,
            Mode: isMode(candidate.Mode) ? candidate.Mode : 'default',
            Color: normalizedColor(candidate.Color),
            ...(candidate.MaterialID ? { MaterialID: candidate.MaterialID } : {}),
            Ord: entries.length + 1,
        });
    }
    [all3DAssets, allAssetTags].forEach(special => {
        const sourceEntry = source.Entries?.find(entry =>
            normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(special.ID));
        entries.push({
            AssetSetID: special.ID,
            Mode: isMode(sourceEntry?.Mode) ? sourceEntry.Mode : 'default',
            Color: normalizedColor(sourceEntry?.Color),
            ...(sourceEntry?.MaterialID ? { MaterialID: sourceEntry.MaterialID } : {}),
            Ord: entries.length + 1,
        });
    });

    const next: IAssetSetDisplayConfiguration = {
        SchemaVersion: DISPLAY_CONFIGURATION_SCHEMA_VERSION,
        FacilityID: source.FacilityID,
        All3DAssetsID: all3DAssets.ID,
        AllAssetTagsID: allAssetTags.ID,
        Entries: entries,
    };
    return source.SchemaVersion === DISPLAY_CONFIGURATION_SCHEMA_VERSION &&
        source.All3DAssetsID === all3DAssets.ID && source.AllAssetTagsID === allAssetTags.ID &&
        sameEntries(source.Entries ?? [], entries)
        ? source
        : next;
};

const mutateEntries = (
    source: IAssetSetDisplayConfiguration,
    mutate: (entries: IAssetSetDisplayEntry[]) => void
): IAssetSetDisplayConfiguration => {
    const entries = source.Entries.map(entry => ({ ...entry }));
    mutate(entries);
    const specialKeys = new Set([
        normalizeDisplayID(source.All3DAssetsID),
        normalizeDisplayID(source.AllAssetTagsID),
    ]);
    const specialEntries = [source.All3DAssetsID, source.AllAssetTagsID].flatMap(id => {
        const entry = entries.find(item => normalizeDisplayID(item.AssetSetID) === normalizeDisplayID(id));
        return entry ? [entry] : [];
    });
    const ordinary = entries.filter(entry => !specialKeys.has(normalizeDisplayID(entry.AssetSetID)));
    return {
        ...source,
        Entries: [...ordinary, ...specialEntries]
            .map((entry, index) => ({ ...entry, Ord: index + 1 })),
    };
};

export const addDisplayEntryAtTop = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string,
    mode: AssetSetDisplayMode,
    initialColor?: number | null
) => mutateEntries(source, entries => {
    const key = normalizeDisplayID(assetSetID);
    if (key === normalizeDisplayID(source.All3DAssetsID) || key === normalizeDisplayID(source.AllAssetTagsID)) return;
    const existingIndex = entries.findIndex(entry => normalizeDisplayID(entry.AssetSetID) === key);
    const existing = existingIndex >= 0 ? entries.splice(existingIndex, 1)[0] : undefined;
    entries.unshift({
        AssetSetID: assetSetID,
        Mode: mode,
        Color: mode === 'color' ? normalizedColor(initialColor) ?? GOLDEN_DISPLAY_COLOR : normalizedColor(initialColor),
        ...(existing?.MaterialID ? { MaterialID: existing.MaterialID } : {}),
        Ord: 1,
    });
});

export const setDisplayEntryMode = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string,
    mode: AssetSetDisplayMode,
    preferredColor?: number | null
) => mutateEntries(source, entries => {
    const key = normalizeDisplayID(assetSetID);
    const entry = entries.find(item => normalizeDisplayID(item.AssetSetID) === key);
    if (!entry) return;
    entry.Mode = mode;
    if (mode === 'color' && normalizedColor(entry.Color) == null) {
        entry.Color = normalizedColor(preferredColor) ?? GOLDEN_DISPLAY_COLOR;
    }
});

export const setDisplayEntryColor = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string,
    color: number
) => mutateEntries(source, entries => {
    const key = normalizeDisplayID(assetSetID);
    const entry = entries.find(item => normalizeDisplayID(item.AssetSetID) === key);
    if (!entry) return;
    entry.Color = normalizedColor(color);
    entry.Mode = 'color';
});

export const reorderDisplayEntry = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string,
    targetIndex: number
) => mutateEntries(source, entries => {
    const key = normalizeDisplayID(assetSetID);
    if (key === normalizeDisplayID(source.All3DAssetsID) || key === normalizeDisplayID(source.AllAssetTagsID)) return;
    const index = entries.findIndex(entry => normalizeDisplayID(entry.AssetSetID) === key);
    if (index < 0) return;
    const [entry] = entries.splice(index, 1);
    const ordinaryCount = entries.filter(item => {
        const itemKey = normalizeDisplayID(item.AssetSetID);
        return itemKey !== normalizeDisplayID(source.All3DAssetsID) && itemKey !== normalizeDisplayID(source.AllAssetTagsID);
    }).length;
    entries.splice(Math.max(0, Math.min(targetIndex, ordinaryCount)), 0, entry);
});

export const removeDisplayEntry = (
    source: IAssetSetDisplayConfiguration,
    assetSetID: string
) => mutateEntries(source, entries => {
    const key = normalizeDisplayID(assetSetID);
    if (key === normalizeDisplayID(source.All3DAssetsID) || key === normalizeDisplayID(source.AllAssetTagsID)) return;
    const index = entries.findIndex(entry => normalizeDisplayID(entry.AssetSetID) === key);
    if (index < 0) return;
    // Material assignments are database-owned. Removing their UI row would cause the next
    // Knowledge View save to delete the assignment, so the client deliberately preserves it.
    if (entries[index].MaterialID) return;
    entries.splice(index, 1);
});

export const resetDisplayConfiguration = (source: IAssetSetDisplayConfiguration) => {
    const createResetFallback = (assetSetID: string): IAssetSetDisplayEntry => {
        const materialID = source.Entries.find(entry =>
            normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(assetSetID))?.MaterialID;
        return {
            AssetSetID: assetSetID,
            Mode: 'default',
            Color: null,
            ...(materialID ? { MaterialID: materialID } : {}),
            Ord: 0,
        };
    };

    return {
        ...source,
        Entries: [
        ...source.Entries
            .filter(entry => entry.MaterialID &&
                normalizeDisplayID(entry.AssetSetID) !== normalizeDisplayID(source.All3DAssetsID) &&
                normalizeDisplayID(entry.AssetSetID) !== normalizeDisplayID(source.AllAssetTagsID))
            .map(entry => ({ ...entry, Mode: 'default' as const, Color: null })),
            createResetFallback(source.All3DAssetsID),
            createResetFallback(source.AllAssetTagsID),
        ].map((entry, index) => ({ ...entry, Ord: index + 1 })),
    };
};

export const hasOrdinaryDisplayEntries = (source: IAssetSetDisplayConfiguration | null | undefined): boolean => {
    if (!source) return false;
    const specialKeys = new Set([
        normalizeDisplayID(source.All3DAssetsID),
        normalizeDisplayID(source.AllAssetTagsID),
    ]);
    return source.Entries.some(entry => !specialKeys.has(normalizeDisplayID(entry.AssetSetID)));
};

export const hasNonDefaultFallbackEntries = (
    source: IAssetSetDisplayConfiguration | null | undefined
): boolean => {
    if (!source) return false;
    const specialKeys = new Set([
        normalizeDisplayID(source.All3DAssetsID),
        normalizeDisplayID(source.AllAssetTagsID),
    ]);
    return source.Entries.some(entry =>
        specialKeys.has(normalizeDisplayID(entry.AssetSetID)) && entry.Mode !== 'default');
};

/**
 * Removes every explicitly displayed Facility Asset Set while preserving the
 * two built-in fallback rows exactly as configured.
 */
export const clearOrdinaryDisplayEntries = (
    source: IAssetSetDisplayConfiguration
): IAssetSetDisplayConfiguration => {
    const specialKeys = new Set([
        normalizeDisplayID(source.All3DAssetsID),
        normalizeDisplayID(source.AllAssetTagsID),
    ]);
    return {
        ...source,
        Entries: source.Entries
            .filter(entry => specialKeys.has(normalizeDisplayID(entry.AssetSetID)))
            .map((entry, index) => ({ ...entry, Ord: index + 1 })),
    };
};

export const resetDisplayConfigurationForOntologySwitch = (
    source: IAssetSetDisplayConfiguration
): IAssetSetDisplayConfiguration =>
    resetDisplayConfiguration(clearOrdinaryDisplayEntries(source));

export interface IAssetSetDisplayDefinition {
    id: string;
    priority: number;
    mode: AssetSetDisplayMode;
    colorKey?: number;
    materialID?: string | null;
    tagIDs: string[];
    isAll3DAssets: boolean;
    isAllAssetTags: boolean;
}

export const buildAssetSetDisplayDefinitions = (
    configuration: IAssetSetDisplayConfiguration | null,
    assetSetsByID: ReadonlyMap<string, IAssetSet>
): IAssetSetDisplayDefinition[] => configuration?.Entries.flatMap((entry, index) => {
    const assetSet = assetSetsByID.get(normalizeDisplayID(entry.AssetSetID)) ?? assetSetsByID.get(entry.AssetSetID);
    if (!assetSet) return [];
    return [{
        id: assetSet.ID,
        priority: index + 1,
        mode: entry.Mode,
        colorKey: entry.Mode === 'color' ? normalizedColor(entry.Color) ?? GOLDEN_DISPLAY_COLOR : undefined,
        materialID: entry.MaterialID ?? null,
        tagIDs: assetSet.AssetTagIDs ?? [],
        isAll3DAssets: is3DFallbackAssetSet(assetSet),
        isAllAssetTags: isAssetTagFallbackAssetSet(assetSet),
    }];
}) ?? [];

/** Fixed top-row swatch palette offered in the Color mode editor. */
export const DISPLAY_COLOR_PALETTE: readonly number[] = [
    GOLDEN_DISPLAY_COLOR,
    0xEE3B33FF,
    0xE31C6EFF,
    0x9126C6FF,
    0x1E9BD7FF,
    0x17C1D1FF,
    0x51B995FF,
    0xF6852CFF,
];

export interface IAssetSetDisplayAppearance {
    mode: AssetSetDisplayMode;
    color: number | null;
    /** True when the Asset Set has no entry and inherits the All Assets fallback. */
    isInherited: boolean;
}

/** Row appearance for an Asset Set leaf: its own entry, or the All Assets fallback. */
export const getAssetSetDisplayAppearance = (
    assetSetID: string,
    configuration: IAssetSetDisplayConfiguration,
    fallbackAssetSetID = configuration.All3DAssetsID,
): IAssetSetDisplayAppearance => {
    const key = normalizeDisplayID(assetSetID);
    const own = configuration.Entries.find(entry => normalizeDisplayID(entry.AssetSetID) === key);
    const source = own ?? configuration.Entries.find(entry =>
        normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(fallbackAssetSetID));
    return {
        mode: source?.Mode ?? 'default',
        color: source?.Mode === 'color' ? normalizedColor(source.Color) ?? GOLDEN_DISPLAY_COLOR : null,
        isInherited: !own,
    };
};

export type TreeVisibilityState = 'invisible' | 'partial' | 'visible';

export const getTreeVisibilityState = (
    descendantAssetSetIDs: readonly string[],
    configuration: IAssetSetDisplayConfiguration,
    fallbackAssetSetID = configuration.All3DAssetsID,
): TreeVisibilityState => {
    const entriesByID = new Map(configuration.Entries.map(entry =>
        [normalizeDisplayID(entry.AssetSetID), entry] as const));
    const fallbackVisible = entriesByID.get(normalizeDisplayID(fallbackAssetSetID))?.Mode !== 'hidden';
    const unique = [...new Set(descendantAssetSetIDs.map(normalizeDisplayID))];
    const visibleCount = unique.filter(id => {
        const entry = entriesByID.get(id);
        return entry ? entry.Mode !== 'hidden' : fallbackVisible;
    }).length;
    if (!unique.length || visibleCount === unique.length) return 'visible';
    if (visibleCount === 0) return 'invisible';
    return 'partial';
};

export const numberToDisplayHex = (color?: number | null): string =>
    color == null ? '' : `#${(color >>> 0).toString(16).padStart(8, '0')}`;

export const displayHexToNumber = (color?: string | null): number | null => {
    if (!color) return null;
    return parseInt(color.replace('#', '').padEnd(8, 'f'), 16) >>> 0;
};
